import {Component, OnInit, ViewChild, OnDestroy} from '@angular/core';
import {DxDataGridComponent} from 'devextreme-angular';
import CustomStore from 'devextreme/data/custom_store';
import {DragulaService} from 'ng2-dragula';
import {Subject} from 'rxjs';
import {take, takeUntil} from 'rxjs/operators';
import {UserApi} from '../../../api';
import {ServiceMasterMappingApi} from '../../../api/service-master-mapping.api';
import {
	GridSetting,
	GridSettings,
	GridState,
	ServiceMappingToMasterGridResult,
	User
} from '../../../shared';
import {MappingAction, MappingMode} from '../../../shared/enum/mapping-mode.enum';
import {ProductMappingOption} from '../../../shared/models/product-mapping-option';
import {ExtraFilteringOptions, FilterOperationModel, LoadOptions} from '../../../shared/models/shared.model';
import * as moment from 'moment';
import {LoaderService} from '../../../core/loader/loader.service';
import {NotificationService} from '../../../service/notification.service';
import {NotificationType} from '../../../shared/enum/notification.enum';
import {FilterBuilderService} from '../../../service/filter-builder.service';

@Component ({
	selector: 'app-service-mapping',
	templateUrl: './service-mapping.component.html',
	styleUrls: ['service-mapping.component.css']
})
export class ServiceMappingComponent implements OnInit, OnDestroy {
	@ViewChild ('serviceMappingLightDataGrid') serviceMappingLightDataGrid: DxDataGridComponent;
	@ViewChild ('serviceMasterExpandedDataGrid') serviceMasterExpandedDataGrid: DxDataGridComponent;

	public serviceMappingLightGridDataSource: any = {}; // 1st grid
	public serviceMappingToMasterGridDataSource: ServiceMappingToMasterGridResult[]; // 2nd grid
	public serviceMasterExpandedDataSource: any = {}; // 3rd grid

	public serviceMappingLightGridSelectedRowMap: any; // first grid selected row
	public serviceMasterExpandedSelectedRowMap: any; // 3rd grid selected row

	public currentUser: User;
	public extraFilteringOptions: ExtraFilteringOptions[] = [
		{column: 'Active', value: false},
	];

	public selectedServiceMasterExpandedGridRow: any;
	public serviceMappingLightGridSelectedRowData: any;

	public createdByHeaderDataSource;
	public createdOnHeaderDataSource;
	public modifiedByHeaderDataSource;
	public modifiedOnHeaderDataSource;
	public reviewedByHeaderDataSource;
	public reviewedOnHeaderDataSource;
	public mappingModeNameHeaderDataSource;
	public mappingAssignedToHeaderDataSource;

	public relatedTermsHeaderDataSource;
	public definitionHeaderDataSource;
	public subSegmentNameHeaderDataSource;
	public segmentNameHeaderDataSource;
	public serviceTypeNameHeaderDataSource;
	public segmentIdHeaderDataSource;
	public serviceTypeIdHeaderDataSource;
	public subSegmentIdHeaderDataSource;
	public mappingPriorityHeaderDataSource;
	public segmentNameMappingHeaderDataSource;
	public serviceTypeMappingNameHeaderDataSource;
	public subSegmentMappingNameHeaderDataSource;
	public relatedTermsMappingHeaderDataSource;

	public mappingDataGridCurrentPage: number;
	public serviceMappingLoadingOptions: LoadOptions;

	private _destroyed$ = new Subject ();
	private isMoveToNextRow = false;
	private serviceMappingLightGridSelectedRowIndex: number;
	private isServiceMappingDataLoading = false;
	private isServiceMappingToMasterDataLoading = false;
	private isServiceMasterDataLoading = false;
	private serviceMasterLoadingOptions: any;

	public isLoadServiceMappingGridDataSource: boolean;
	public isLoadServiceMasterGridDataSource: boolean;
	private readonly mappingGridName: string = 'serviceMappingDataGrid';
	private readonly masterGridName: string = 'serviceMasterDataGrid';
	private readonly pageName: string = 'serviceMapping';
	private currentServiceMappingDataGridState: GridState;
	private currentServiceMasterDataGridState: GridState;
	private serviceMappingDataGridSettings: GridSettings;
	private serviceMasterDataGridSettings: GridSettings;
	private isServiceMappingDataGridSettingLoading = true;
	private isServiceMasterDataGridSettingLoading = true;
	private serviceMappingDataGridSetting: GridSetting = new GridSetting ();
	private serviceMasterDataGridSetting: GridSetting = new GridSetting ();

	constructor(
		private serviceMasterMappingApi: ServiceMasterMappingApi,
		private userService: UserApi,
		public dragulaService: DragulaService,
		private loaderService: LoaderService,
		private notificationService: NotificationService,
		private filterBuilder: FilterBuilderService
	) {
		dragulaService.createGroup ('HANDLES2', {
			moves: (el, container, handle) => {
				return handle.className === 'fa fa-arrows';
			}
		});
	}

	ngOnInit() {
		this.loaderService.show ();
		this.currentUser = this.userService.logonUser;
		if (!this.currentUser) {
			return;
		}
		this.serviceMappingDataGridSettings = {
			PageName: this.pageName,
			GridName: this.mappingGridName,
			OwnedByUserId: this.currentUser.UserId
		};
		this.userService.getGridSettings (this.serviceMappingDataGridSettings)
		.pipe (take (1))
		.subscribe ((settings) => {
			if (settings && settings.Settings) {
				if (typeof settings.Settings === 'string') {
					this.serviceMappingDataGridSetting = JSON.parse (settings.Settings);
				} else {
					this.serviceMappingDataGridSetting = settings.Settings;
				}
			} else {
				this.serviceMappingDataGridSettings.Settings = JSON.stringify ({autosave: true});
				this.updateGridSettings (this.serviceMappingDataGridSettings);
			}
			this.loadServiceMappingDataGridState ();
		});
		this.loadColumnHeaderFilterData ();

		this.serviceMasterDataGridSettings = {
			PageName: this.pageName,
			GridName: this.masterGridName,
			OwnedByUserId: this.currentUser.UserId
		};

		this.userService.getGridSettings (this.serviceMasterDataGridSettings)
		.pipe (take (1))
		.subscribe ((settings) => {
			if (settings && settings.Settings) {
				if (typeof settings.Settings === 'string') {
					this.serviceMasterDataGridSetting = JSON.parse (settings.Settings);
				} else {
					this.serviceMasterDataGridSetting = settings.Settings;
				}
			} else {
				this.serviceMasterDataGridSettings.Settings = JSON.stringify ({autosave: true});
				this.updateGridSettings (this.serviceMasterDataGridSettings);
			}
			this.loadServiceMasterDataGridState ();
		});

		this.loadMasterGridColumnHeaderFilterData();
	}

	ngOnDestroy(): void {
		this.dragulaService.destroy ('HANDLES2');
	}

	private loadServiceMappingDataGridState() {
		const serviceMappingDataGridState: GridState = {
			PageName: this.pageName,
			GridName: this.mappingGridName,
			OwnedByUserId: this.currentUser.UserId
		};
		this.userService.getGridState (serviceMappingDataGridState)
		.pipe (takeUntil (this._destroyed$))
		.subscribe ((state) => {
			if (state) {
				this.currentServiceMappingDataGridState = state;
			} else {
				this.currentServiceMappingDataGridState = {
					PageName: this.pageName,
					GridName: this.mappingGridName,
					OwnedByUserId: this.currentUser.UserId,
					State: null
				};
			}
			this.loadServiceMappingLightGridDataSource ();
		});
	}

	public loadMappingState = () => {
		return this.currentServiceMappingDataGridState.State ? JSON.parse (this.currentServiceMappingDataGridState.State) : null;
	};

	public saveMappingState = (state) => {
		if (this.serviceMappingDataGridSetting) {
			let gridSetting: GridSetting;
			if (typeof this.serviceMappingDataGridSetting === 'string') {
				gridSetting = JSON.parse (this.serviceMappingDataGridSetting);
			} else {
				gridSetting = this.serviceMappingDataGridSetting;
			}
			if (gridSetting.autosave) {
				this.currentServiceMappingDataGridState.State = JSON.stringify (state);
				this.updateGridState (this.currentServiceMappingDataGridState);
			}
		}
	};

	private loadServiceMappingLightGridDataSource() {
		this.isServiceMappingDataGridSettingLoading = false;
		this.serviceMappingLightGridDataSource = new CustomStore ({
			key: 'ServiceAndProductMappingId',
			load: (loadOptions: any) => {
				this.isServiceMappingDataLoading = true;
				this.loaderService.show ();
				const filter = this.formatFilterOperators (loadOptions.filter);
				const finalOptions: LoadOptions = loadOptions;
				finalOptions.filter = filter;
				finalOptions.extraParams = this.extraFilteringOptions;
				this.serviceMappingLoadingOptions = finalOptions;

				return this.serviceMasterMappingApi.getServiceMappingGrid (finalOptions)
				.toPromise ()
				.then ((data: any) => {
					this.isServiceMappingDataLoading = false;
					this.loaderHandler ();
					if (this.serviceMappingLightGridSelectedRowData) {
						const matchedData =
							data.data.find (item => item.ServiceAndProductMappingId === this.serviceMappingLightGridSelectedRowData.ServiceAndProductMappingId);
						this.isMoveToNextRow = matchedData ? true : false;
					}
					return {
						data: data.data,
						totalCount: data.totalCount,
						summary: data.summary,
						groupCount: data.groupCount
					};
				})
				.catch (error => {
					this.isServiceMappingDataLoading = false;
					this.loaderHandler ();
					throw new Error ('Data Loading Error');
				});
			}
		});
		this.isLoadServiceMappingGridDataSource = true;
	}

	private loadServiceMasterDataGridState() {
		const productMasterDataGridState: GridState = {
			PageName: this.pageName,
			GridName: this.masterGridName,
			OwnedByUserId: this.currentUser.UserId
		};
		this.userService.getGridState (productMasterDataGridState)
		.pipe (takeUntil (this._destroyed$))
		.subscribe ((state) => {
			if (state) {
				this.currentServiceMasterDataGridState = state;
			} else {
				this.currentServiceMasterDataGridState = {
					PageName: this.pageName,
					GridName: this.masterGridName,
					OwnedByUserId: this.currentUser.UserId,
					State: null
				};
			}
			this.loadServiceMasterExpandedDataSource ();
		});
	}

	public loadMasterState = () => {
		return this.currentServiceMasterDataGridState.State ? JSON.parse (this.currentServiceMasterDataGridState.State) : null;
	};

	public saveMasterState = (state) => {
		if (this.serviceMasterDataGridSetting) {
			let gridSetting: GridSetting;
			if (typeof this.serviceMasterDataGridSetting === 'string') {
				gridSetting = JSON.parse (this.serviceMasterDataGridSetting);
			} else {
				gridSetting = this.serviceMasterDataGridSetting;
			}
			if (gridSetting.autosave) {
				this.currentServiceMasterDataGridState.State = JSON.stringify (state);
				this.updateGridState (this.currentServiceMasterDataGridState);
			}
		}
	};

	private loadServiceMasterExpandedDataSource() {
		this.isServiceMasterDataGridSettingLoading = false;
		this.serviceMasterExpandedDataSource = new CustomStore ({
			key: 'ServiceMasterId',
			load: (loadOptions: any) => {
				this.isServiceMasterDataLoading = true;
				this.loaderService.show ();
				const filter = this.formatFilterOperators (loadOptions.filter);
				const finalOptions: LoadOptions = loadOptions;
				finalOptions.filter = filter;
				this.serviceMasterLoadingOptions = filter;

				return this.serviceMasterMappingApi.getServiceMasterExpanded (finalOptions)
				.toPromise ()
				.then ((data: any) => {
					this.isServiceMasterDataLoading = false;
					this.loaderHandler ();
					return {
						data: data.data,
						totalCount: data.totalCount,
						summary: data.summary,
						groupCount: data.groupCount
					};
				})
				.catch (error => {
					this.isServiceMasterDataLoading = false;
					this.loaderHandler ();
					throw new Error ('Data Loading Error');
				});
			}
		});
		this.isLoadServiceMasterGridDataSource = true;
	}

	private updateGridSettings(gridSettings: GridSettings) {
		gridSettings.SavedOn = new Date ();
		this.userService.setGridSettings (gridSettings).pipe (take (1)).subscribe ();
	}

	private updateGridState(gridState: GridState) {
		gridState.SavedOn = new Date ();
		this.userService.setGridState (gridState).pipe (take (1)).subscribe ();
	}

	getHeaderFiltersServiceMapping(loadingOption) {
		return this.serviceMasterMappingApi.getHeaderFiltersServiceMapping (loadingOption)
		.toPromise ()
		.catch (error => {
			throw new Error ('Data Loading Error');
		});
	}

	getHeaderFiltersServiceMaster(loadingOption) {
		return this.serviceMasterMappingApi.getHeaderFiltersServiceMaster (loadingOption)
		.toPromise ()
		.catch (error => {
			throw new Error ('Data Loading Error');
		});
	}

	customizeServiceMappingId(cellInfo) {
		return 'SeAPMp_' + cellInfo.value;
	}

	customizeServiceMasterId(cellInfo) {
		return 'SeMs_' + cellInfo.value;
	}

	dateTimeFieldCustomization(cellInfo) {
		const value = cellInfo.value;
		const date = moment (value, 'M/d/yyyy, HH:mm', true).isValid ();
		const minDate = moment.utc ('0001-01-01');

		if (date && moment.utc (value).isAfter (minDate)) {
			return cellInfo.valueText;
		} else {
			return '';
		}

		// if(cellInfo.target === "filterRow" && value) {
		//   return (value.getMonth() + 1) + "/" + value.getDate() + "/" + value.getFullYear();
		// }
	}

	selectionChangedHandler() {
		if (this.serviceMappingLightDataGrid.instance.getSelectedRowsData ()[0]) {
			this.serviceMappingLightGridSelectedRowData = this.serviceMappingLightDataGrid.instance.getSelectedRowsData ()[0];
		}
		const serviceAndProductMappingId = this.serviceMappingLightGridSelectedRowMap[0];
		if (serviceAndProductMappingId) {
			this.getServiceMappingToMasterGrid (serviceAndProductMappingId);
		}
		this.setServiceMappingLightGridSelectedRowIndex ();
	}

	getServiceMappingToMasterGrid(serviceAndProductMappingId) {
		if (!serviceAndProductMappingId) {
			return;
		}
		this.isServiceMappingToMasterDataLoading = true;
		this.loaderService.show ();
		this.serviceMasterMappingApi.getServiceMappingToMasterGrid (serviceAndProductMappingId)
		.pipe (takeUntil (this._destroyed$))
		.subscribe ((res: ServiceMappingToMasterGridResult[]) => {
			this.isServiceMappingToMasterDataLoading = false;
			this.loaderHandler ();

			this.serviceMappingToMasterGridDataSource = res;
		});
	}

	serviceMasterExpandedSelectionChangedHandler() {
		if (this.serviceMasterExpandedDataGrid.instance.getSelectedRowsData ()[0]) {
			this.selectedServiceMasterExpandedGridRow = this.serviceMasterExpandedDataGrid.instance.getSelectedRowsData ()[0];
		}
	}

	onRowExpanding(e: any) {
		e.component.collapseAll (-1);
	}

	extraFilteringOptionsChange(columnName, event) {
		if (event) {
			if (columnName === 'Active') {
				const option = this.extraFilteringOptions.find (item => item.column === columnName);
				option.value = !event.value;
			} else {
				const option = this.extraFilteringOptions.find (item => item.column === columnName);
				option.value = event.value;
			}
			this.serviceMappingLightDataGrid.instance.deselectAll ();
			this.refreshGrid ();
		}
	}

	onReady(e) {
		const grid = e.component;
		const selection = grid.getSelectedRowKeys ();
		if (this.serviceMappingLightGridSelectedRowMap) {
			const currentDataItems = this.serviceMappingLightDataGrid.instance.getDataSource ().items ();
			const isSelectedRowExists = currentDataItems.find (item =>
				item.ServiceAndProductMappingId ===
				this.serviceMappingLightGridSelectedRowMap[0]
			);
			if (!isSelectedRowExists && this.mappingDataGridCurrentPage !== this.serviceMappingLightDataGrid.instance.pageIndex ()) {
				grid.selectRowsByIndexes ([0]);
			}
		}
		if (selection.length === 0) {
			grid.selectRowsByIndexes ([0]);
		} else {
			if (this.isMoveToNextRow && this.isNextRowAvailable) {
				this.moveToNextRow ();
			} else {
				this.stayInRow ();
			}
			if (this.mappingDataGridCurrentPage) {
				this.isMoveToNextRow = false;
				// this.serviceMappingLightDataGrid.instance.pageIndex(this.mappingDataGridCurrentPage);
			}
		}
	}

	clear() {
		const mappingData: ProductMappingOption = {};
		mappingData.MappingModeId = MappingMode.New;
		mappingData.ModifiedByUserId = this.currentUser.UserId;
		mappingData.ServiceAndProductMappingId = this.serviceMappingLightGridSelectedRowData.ServiceAndProductMappingId;
		mappingData.ActionType = MappingAction.Clear;
		this.serviceMasterMappingApi.mappingAction (mappingData)
		.pipe (takeUntil (this._destroyed$))
		.subscribe ((res) => {
			if (res) {
				this.refreshGrid ();
			}
		});
	}

	omit() {
		const mappingData: ProductMappingOption = {};
		mappingData.MappingModeId = MappingMode.Omitted;
		mappingData.ModifiedByUserId = this.currentUser.UserId;
		mappingData.ServiceAndProductMappingId = this.serviceMappingLightGridSelectedRowData.ServiceAndProductMappingId;
		mappingData.ActionType = MappingAction.Omit;
		this.loaderService.show ();
		this.serviceMasterMappingApi.mappingAction (mappingData)
		.pipe (takeUntil (this._destroyed$))
		.subscribe ((res) => {
			if (res) {
				this.refreshGrid ();
				this.notificationService.show ('Omitted successfully!', NotificationType.Success);
			} else {
				this.notificationService.show ('Something went wrong', NotificationType.Error);
				this.loaderHandler ();
			}
		});
	}

	save() {
		const mappingData: ProductMappingOption = {};
		mappingData.MappingModeId = MappingMode.Saved;
		mappingData.ModifiedByUserId = this.currentUser.UserId;
		mappingData.ServiceAndProductMappingId = this.serviceMappingLightGridSelectedRowData.ServiceAndProductMappingId;
		mappingData.ActionType = MappingAction.Save;
		this.loaderService.show ();
		this.serviceMasterMappingApi.mappingAction (mappingData)
		.pipe (takeUntil (this._destroyed$))
		.subscribe ((res) => {
			if (res) {
				this.refreshGrid ();
				this.notificationService.show ('Accept saved successfully!', NotificationType.Success);
			} else {
				this.notificationService.show ('Something went wrong', NotificationType.Error);
				this.loaderHandler ();
			}
		});
	}

	map(serviceMasterId) {
		const mappingData: ProductMappingOption = {};
		mappingData.MappingModeId = MappingMode.Mapped;
		mappingData.ModifiedByUserId = this.currentUser.UserId;
		mappingData.ServiceAndProductMappingId = this.serviceMappingLightGridSelectedRowData.ServiceAndProductMappingId;
		mappingData.ServiceMasterId = serviceMasterId;
		mappingData.ActionType = MappingAction.Map;
		this.loaderService.show ();
		this.serviceMasterMappingApi.mappingAction (mappingData)
		.pipe (takeUntil (this._destroyed$))
		.subscribe ((res) => {
			if (res) {
				this.refreshGrid ();
				this.notificationService.show ('Mapped successfully!', NotificationType.Success);
			} else {
				this.notificationService.show ('Something went wrong', NotificationType.Error);
				this.loaderHandler ();
			}
		});
	}

	accept() {
		const mappingData: ProductMappingOption = {};
		mappingData.MappingModeId = MappingMode.Mapped;
		mappingData.ModifiedByUserId = this.currentUser.UserId;
		mappingData.ServiceAndProductMappingId = this.serviceMappingLightGridSelectedRowData.ServiceAndProductMappingId;
		mappingData.ActionType = MappingAction.Accept;
		this.loaderService.show ();
		this.serviceMasterMappingApi.mappingAction (mappingData)
		.pipe (takeUntil (this._destroyed$))
		.subscribe ((res) => {
			if (res) {
				this.refreshGrid ();
				this.notificationService.show ('Accepted successfully!', NotificationType.Success);
			} else {
				this.notificationService.show ('Something went wrong', NotificationType.Error);
				this.loaderHandler ();
			}
		});
	}

	review() {
		const mappingData: ProductMappingOption = {};
		mappingData.ModifiedByUserId = this.currentUser.UserId;
		mappingData.ServiceAndProductMappingId = this.serviceMappingLightGridSelectedRowData.ServiceAndProductMappingId;
		mappingData.ActionType = MappingAction.Review;
		this.loaderService.show ();
		this.serviceMasterMappingApi.mappingAction (mappingData)
		.pipe (takeUntil (this._destroyed$))
		.subscribe ((res) => {
			if (res) {
				this.refreshGrid ();
				this.notificationService.show ('Review successfully!', NotificationType.Success);
			} else {
				this.notificationService.show ('Something went wrong', NotificationType.Error);
				this.loaderHandler ();
			}
		});
	}

	acceptMapping(serviceMasterId) {
		const mappingData: ProductMappingOption = {};
		mappingData.MappingModeId = MappingMode.New;
		mappingData.ModifiedByUserId = this.currentUser.UserId;
		mappingData.ServiceAndProductMappingId = this.serviceMappingLightGridSelectedRowData.ServiceAndProductMappingId;
		mappingData.ServiceMasterId = serviceMasterId;
		mappingData.ActionType = MappingAction.AcceptMapping;
		this.loaderService.show ();
		this.serviceMasterMappingApi.mappingAction (mappingData)
		.pipe (takeUntil (this._destroyed$))
		.subscribe ((res) => {
			if (res) {
				this.refreshGrid ();
				this.notificationService.show ('Accept Mapping successfully!', NotificationType.Success);
			} else {
				this.notificationService.show ('Something went wrong', NotificationType.Error);
				this.loaderHandler ();
			}
		});
	}

	mapAsProduct() {
		const mappingData: ProductMappingOption = {};
		mappingData.MappingModeId = MappingMode.New;
		mappingData.MappingAsService = true;
		mappingData.ModifiedByUserId = this.currentUser.UserId;
		mappingData.ServiceAndProductMappingId = this.serviceMappingLightGridSelectedRowData.ServiceAndProductMappingId;
		mappingData.ActionType = MappingAction.MapAsProduct;
		this.loaderService.show ();
		this.serviceMasterMappingApi.mappingAction (mappingData)
		.pipe (takeUntil (this._destroyed$))
		.subscribe ((res) => {
			if (res) {
				this.refreshGrid ();
				this.notificationService.show ('Moved to Product successfully!', NotificationType.Success);
			} else {
				this.notificationService.show ('Something went wrong', NotificationType.Error);
				this.loaderHandler ();
			}
		});
	}

	deactivate(serviceMasterId) {
		const mappingData: ProductMappingOption = {};
		mappingData.ModifiedByUserId = this.currentUser.UserId;
		mappingData.Active = false;
		mappingData.ServiceAndProductMappingId = serviceMasterId;
		mappingData.ActionType = MappingAction.Deactivate;
		this.loaderService.show ();
		this.serviceMasterMappingApi.mappingAction (mappingData)
		.pipe (takeUntil (this._destroyed$))
		.subscribe ((res) => {
			if (res) {
				this.refreshGrid ();
				this.notificationService.show ('Deactivated successfully!', NotificationType.Success);
			} else {
				this.notificationService.show ('Something went wrong', NotificationType.Error);
				this.loaderHandler ();
			}
		});
	}

	reactivate(serviceMasterId) {
		const mappingData: ProductMappingOption = {};
		mappingData.ModifiedByUserId = this.currentUser.UserId;
		mappingData.Active = true;
		mappingData.ServiceAndProductMappingId = serviceMasterId;
		mappingData.ActionType = MappingAction.Reactivate;
		this.loaderService.show ();
		this.serviceMasterMappingApi.mappingAction (mappingData)
		.pipe (takeUntil (this._destroyed$))
		.subscribe ((res) => {
			if (res) {
				this.refreshGrid ();
				this.notificationService.show ('Reactivated successfully!', NotificationType.Success);
			} else {
				this.notificationService.show ('Something went wrong', NotificationType.Error);
				this.loaderHandler ();
			}
		});
	}

	omitBarcode() {
		const mappingData: ProductMappingOption = {};
		mappingData.MappingModeId = MappingMode.Omitted;
		mappingData.ModifiedByUserId = this.currentUser.UserId;
		mappingData.ServiceAndProductMappingId = this.serviceMappingLightGridSelectedRowData.ServiceAndProductMappingId;
		mappingData.ActionType = MappingAction.OmitBarcode;
		this.loaderService.show ();
		this.serviceMasterMappingApi.mappingAction (mappingData)
		.pipe (takeUntil (this._destroyed$))
		.subscribe ((res) => {
			if (res) {
				this.refreshGrid ();
				this.notificationService.show ('Barcode omitted!', NotificationType.Success);
			} else {
				this.notificationService.show ('Something went wrong', NotificationType.Error);
				this.loaderHandler ();
			}
		});
	}

	saveBarcode() {
		const mappingData: ProductMappingOption = {};
		mappingData.MappingModeId = MappingMode.Saved;
		mappingData.ModifiedByUserId = this.currentUser.UserId;
		mappingData.ServiceAndProductMappingId = this.serviceMappingLightGridSelectedRowData.ServiceAndProductMappingId;
		mappingData.ActionType = MappingAction.SaveBarcode;
		this.loaderService.show ();
		this.serviceMasterMappingApi.mappingAction (mappingData)
		.pipe (takeUntil (this._destroyed$))
		.subscribe ((res) => {
			if (res) {
				this.refreshGrid ();
				this.notificationService.show ('Barcode saved!', NotificationType.Success);
			} else {
				this.notificationService.show ('Something went wrong', NotificationType.Error);
				this.loaderHandler ();
			}
		});
	}

	refreshGrid() {
		this.mappingDataGridCurrentPage = this.serviceMappingLightDataGrid.instance.pageIndex ();
		this.serviceMappingLightDataGrid.instance.refresh ();
	}

	refreshMasterGrid() {
		this.serviceMasterExpandedDataGrid.instance.refresh ();
	}

	formatFilterOperators(rawFilter: any[]) {
		if (!rawFilter) {
			return null;
		}
		const filterData = this.filterBuilder.handleNotFilterFormatting (rawFilter);
		return this.filterBuilder.filterFormatting (filterData);
	}

	/**
	 * Move selection to the next row
	 */
	private moveToNextRow() {
		this.serviceMappingLightDataGrid.instance.selectRowsByIndexes ([this.serviceMappingLightGridSelectedRowIndex + 1]);
	}

	/**
	 * Stay in the same row
	 */
	private stayInRow() {
		this.serviceMappingLightDataGrid.instance.selectRowsByIndexes ([this.serviceMappingLightGridSelectedRowIndex]);
	}

	/**
	 * Check if next row on mapping datagrid available
	 */
	private get isNextRowAvailable() {
		const productMappingRowData = this.serviceMappingLightDataGrid.instance.getRowElement (this.serviceMappingLightGridSelectedRowIndex + 1);
		if (productMappingRowData) {
			return true;
		}

		return false;
	}

	/**
	 * Load column header filters' data
	 */
	private loadColumnHeaderFilterData() {
		this.createdByHeaderDataSource = new CustomStore ({
			load: (loadOptions: any) => {
				const columnName = 'CreatedByUserFullNameSortableOrPrincipalName';
				loadOptions.column = columnName;
				loadOptions.valueColumn = '';

				return this.getHeaderFiltersServiceMapping (loadOptions);
			}
		});

		this.createdOnHeaderDataSource = new CustomStore ({
			load: (loadOptions: any) => {
				loadOptions.column = 'CreatedOn';
				loadOptions.valueColumn = '';

				return this.getHeaderFiltersServiceMapping (loadOptions);
			}
		});

		this.reviewedOnHeaderDataSource = new CustomStore ({
			load: (loadOptions: any) => {
				const columnName = 'ReviewedOn';
				loadOptions.column = columnName;
				loadOptions.valueColumn = '';

				return this.getHeaderFiltersServiceMapping (loadOptions);
			}
		});

		this.modifiedOnHeaderDataSource = new CustomStore ({
			load: (loadOptions: any) => {
				const columnName = 'ModifiedOn';
				loadOptions.column = columnName;
				loadOptions.valueColumn = '';

				return this.getHeaderFiltersServiceMapping (loadOptions);
			}
		});

		this.modifiedByHeaderDataSource = new CustomStore ({
			load: (loadOptions: any) => {
				const columnName = 'ModifiedByUserFullNameSortableOrPrincipalName';
				loadOptions.column = columnName;
				loadOptions.valueColumn = '';

				return this.getHeaderFiltersServiceMapping (loadOptions);
			}
		});

		this.reviewedByHeaderDataSource = new CustomStore ({
			load: (loadOptions: any) => {
				const columnName = 'ReviewedByUserFullNameSortableOrPrincipalName';
				loadOptions.column = columnName;
				loadOptions.valueColumn = '';

				return this.getHeaderFiltersServiceMapping (loadOptions);
			}
		});

		this.mappingModeNameHeaderDataSource = [
			{
				text: 'New',
				value: 0
			},
			{
				text: 'Map',
				value: 1
			},
			{
				text: 'Save',
				value: 2
			},
			{
				text: 'Omit',
				value: 2
			}
		];

		this.mappingAssignedToHeaderDataSource = new CustomStore ({
			load: (loadOptions: any) => {
				const columnName = 'MappingAssignedToUserFullNameSortableOrPrincipalName';
				loadOptions.column = columnName;
				loadOptions.valueColumn = '';

				return this.getHeaderFiltersServiceMapping (loadOptions);
			}
		});

		this.segmentIdHeaderDataSource = new CustomStore ({
			load: (loadOptions: any) => {
				const columnName = 'SegmentId';
				loadOptions.column = columnName;
				loadOptions.valueColumn = '';

				return this.getHeaderFiltersServiceMapping (loadOptions);
			}
		});

		this.serviceTypeIdHeaderDataSource = new CustomStore ({
			load: (loadOptions: any) => {
				const columnName = 'ServiceTypeId';
				loadOptions.column = columnName;
				loadOptions.valueColumn = '';

				return this.getHeaderFiltersServiceMapping (loadOptions);
			}
		});
		this.subSegmentIdHeaderDataSource = new CustomStore ({
			load: (loadOptions: any) => {
				const columnName = 'SubSegmentId';
				loadOptions.column = columnName;
				loadOptions.valueColumn = '';

				return this.getHeaderFiltersServiceMapping (loadOptions);
			}
		});
		this.mappingPriorityHeaderDataSource = new CustomStore ({
			load: (loadOptions: any) => {
				const columnName = 'MappingPriority';
				loadOptions.column = columnName;
				loadOptions.valueColumn = '';

				return this.getHeaderFiltersServiceMapping (loadOptions);
			}
		});

		this.segmentNameMappingHeaderDataSource = new CustomStore ({
			load: (loadOptions: any) => {
				const columnName = 'SegmentName';
				loadOptions.column = columnName;
				loadOptions.valueColumn = '';

				return this.getHeaderFiltersServiceMapping (loadOptions);
			}
		});

		this.serviceTypeMappingNameHeaderDataSource = new CustomStore ({
			load: (loadOptions: any) => {
				const columnName = 'ServiceTypeName';
				loadOptions.column = columnName;
				loadOptions.valueColumn = '';

				return this.getHeaderFiltersServiceMapping (loadOptions);
			}
		});

		this.subSegmentMappingNameHeaderDataSource = new CustomStore ({
			load: (loadOptions: any) => {
				const columnName = 'SubSegmentName';
				loadOptions.column = columnName;
				loadOptions.valueColumn = '';

				return this.getHeaderFiltersServiceMapping (loadOptions);
			}
		});

		this.relatedTermsMappingHeaderDataSource = new CustomStore ({
			load: (loadOptions: any) => {
				const columnName = 'RelatedTerms';
				loadOptions.column = columnName;
				loadOptions.valueColumn = '';

				return this.getHeaderFiltersServiceMapping (loadOptions);
			}
		});
	}

	private loadMasterGridColumnHeaderFilterData() {
		this.relatedTermsHeaderDataSource = new CustomStore ({
			load: (loadOptions: any) => {
				const columnName = 'RelatedTerms';
				loadOptions.column = columnName;
				loadOptions.valueColumn = '';
				loadOptions.filter = this.clearHeaderFilterParams (this.serviceMasterLoadingOptions, columnName);

				return this.getHeaderFiltersServiceMaster (loadOptions);
			}
		});

		this.serviceTypeNameHeaderDataSource = new CustomStore ({
			load: (loadOptions: any) => {
				const columnName = 'ServiceTypeName';
				loadOptions.column = columnName;
				loadOptions.valueColumn = '';
				loadOptions.filter = this.clearHeaderFilterParams (this.serviceMasterLoadingOptions, columnName);

				return this.getHeaderFiltersServiceMaster (loadOptions);
			}
		});

		this.segmentNameHeaderDataSource = new CustomStore ({
			load: (loadOptions: any) => {
				const columnName = 'SegmentName';
				loadOptions.column = columnName;
				loadOptions.valueColumn = '';
				loadOptions.filter = this.clearHeaderFilterParams (this.serviceMasterLoadingOptions, columnName);

				return this.getHeaderFiltersServiceMaster (loadOptions);
			}
		});

		this.subSegmentNameHeaderDataSource = new CustomStore ({
			load: (loadOptions: any) => {
				const columnName = 'SubSegmentName';
				loadOptions.column = columnName;
				loadOptions.valueColumn = '';
				loadOptions.filter = this.clearHeaderFilterParams (this.serviceMasterLoadingOptions, columnName);

				return this.getHeaderFiltersServiceMaster (loadOptions);
			}
		});

		this.definitionHeaderDataSource = new CustomStore ({
			load: (loadOptions: any) => {
				const columnName = 'Definition';
				loadOptions.column = columnName;
				loadOptions.valueColumn = '';
				loadOptions.filter = this.clearHeaderFilterParams (this.serviceMasterLoadingOptions, columnName);

				return this.getHeaderFiltersServiceMaster (loadOptions);
			}
		});
	}
	/**
	 * Handle when to hide loading panel
	 */
	private loaderHandler() {
		if (
			!this.isServiceMappingDataLoading &&
			!this.isServiceMappingToMasterDataLoading &&
			!this.isServiceMasterDataLoading &&
			!this.isServiceMappingDataGridSettingLoading &&
			!this.isServiceMasterDataGridSettingLoading) {
			this.loaderService.hide ();
		}
	}

	private clearHeaderFilterParams(loadingFilter: FilterOperationModel[], columnName: string) {
		let res = null;
		if (loadingFilter) {
			res = loadingFilter.filter (item => item.column !== columnName);
			return res;
		}
		return res;
	}

	private setServiceMappingLightGridSelectedRowIndex() {
		const selectedRowKey = this.serviceMappingLightGridSelectedRowMap[0];
		const serviceMappingLightGridSelectedRowIndex = this.serviceMappingLightDataGrid.instance.getRowIndexByKey (selectedRowKey);
		this.serviceMappingLightGridSelectedRowIndex =
			serviceMappingLightGridSelectedRowIndex >= 0 ? serviceMappingLightGridSelectedRowIndex : this.serviceMappingLightGridSelectedRowIndex;
	}

	serviceMappingDataGridContextMenu(e) {
		if (e.target == 'content') {
			// e.items can be undefined
			if (!e.items) {
				e.items = [];
			}

			// Add a custom menu item
			e.items.push (
				{
					text: 'Refresh',
					icon: 'refresh',
					onItemClick: () => {
						this.refreshGrid ();
					}
				},
				{
					text: 'Grid Settings',
					items: [
						{
							text: this.serviceMappingDataGridSetting.autosave ? 'Disable autosave' : 'Enable autosave',
							onItemClick: () => {
								this.serviceMappingDataGridSetting.autosave = !this.serviceMappingDataGridSetting.autosave;
								this.serviceMappingDataGridSettings.Settings = JSON.stringify (this.serviceMappingDataGridSetting);
								this.updateGridSettings (this.serviceMappingDataGridSettings);
							}
						},
						{
							text: 'Reset to Default',
							onItemClick: () => {
								this.userService.resetDataGridStateToDefault (this.mappingGridName, this.pageName, this.currentUser.UserId)
								.pipe (take (1))
								.subscribe (() => {
									location.reload();
								});
							}
						}
					]
				});
		}
	}

	serviceMasterDataGridContextMenu(e) {
		if (e.target == 'content') {
			// e.items can be undefined
			if (!e.items) {
				e.items = [];
			}

			// Add a custom menu item
			e.items.push (
				{
					text: 'Refresh',
					icon: 'refresh',
					onItemClick: () => {
						this.refreshMasterGrid ();
					}
				},
				{
					text: 'Grid Settings',
					items: [
						{
							text: this.serviceMasterDataGridSetting.autosave ? 'Disable autosave' : 'Enable autosave',
							onItemClick: () => {
								this.serviceMasterDataGridSetting.autosave = !this.serviceMasterDataGridSetting.autosave;
								this.serviceMasterDataGridSettings.Settings = JSON.stringify (this.serviceMasterDataGridSetting);
								this.updateGridSettings (this.serviceMasterDataGridSettings);
							}
						}
					]
				});
		}
	}
}
